import React, { Fragment, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

import Header from "../components/Header";
import Leftnav from "../components/Leftnav";
import Popupchat from "../components/Popupchat";
import Appfooter from "../components/Appfooter";

import { getClubProfileBySlug } from "../redux/slices/clubSlice";
import { fetchClubMediaSection } from "../redux/slices/clubSectionSlice";
import { RootState } from "../redux/store";

const API_URL = (
  process.env.REACT_APP_API_URL || "https://api.bsangam.com"
)
  .replace(/\/uploads\/?$/, "")
  .replace(/\/$/, "");

const getImageUrl = (file?: string | null) => {
  if (!file) return "/assets/images/user.png";
  const value = String(file).trim();
  if (/^https?:\/\//i.test(value)) return encodeURI(value);
  if (value.startsWith("/uploads/")) return encodeURI(`${API_URL}${value}`);
  if (value.startsWith("uploads/")) return encodeURI(`${API_URL}/${value}`);
  if (value.startsWith("/")) return encodeURI(`${API_URL}${value}`);
  return encodeURI(`${API_URL}/uploads/${value}`);
};

const formatDate = (value?: string | null) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const niceLabel = (key: string) =>
  key
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const MediaAlbumDetailsPage: React.FC = () => {
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const { slug, albumId } = useParams();

  const { clubProfile, profileLoading } = useSelector(
    (state: RootState) => state.club
  );

  const { mediaData, mediaLoading } = useSelector(
    (state: RootState) => state.clubSection
  );

  const loggedUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "null");
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    if (slug) {
      dispatch(getClubProfileBySlug(slug));
    }
  }, [dispatch, slug]);

  useEffect(() => {
    if (!clubProfile?.client_id) return;

    dispatch(
      fetchClubMediaSection({
        client_id: Number(clubProfile.client_id),
        user_id: loggedUser?.user_id
          ? Number(loggedUser.user_id)
          : undefined,
      })
    );
  }, [dispatch, clubProfile?.client_id, loggedUser?.user_id]);

  const mediaList = Array.isArray(mediaData?.data) ? mediaData.data : [];

  const album = mediaList.find(
    (item: any) => String(item.album_id) === String(albumId)
  );

  const images = album
    ? Array.isArray(album.album_image_urls)
      ? album.album_image_urls
      : Array.isArray(album.album_images)
      ? album.album_images
      : []
    : [];

  const videos = album
    ? Array.isArray(album.video_links)
      ? album.video_links
      : Array.isArray(album.video_urls)
      ? album.video_urls
      : []
    : [];

  const excludedKeys = new Set([
    "album_id",
    "album_title",
    "album_description",
    "description",
    "cover_image",
    "cover_image_url",
    "album_images",
    "album_image_urls",
    "video_links",
    "video_urls",
  ]);

  const extraFields = album
    ? Object.entries(album).filter(
        ([key, value]) =>
          !excludedKeys.has(key) &&
          !key.toLowerCase().endsWith("_id") &&
          key.toLowerCase() !== "id" &&
          value !== null &&
          value !== undefined &&
          value !== "" &&
          typeof value !== "object"
      )
    : [];

  const loading = profileLoading || mediaLoading;

  return (
    <Fragment>
      <Header />
      <Leftnav />

      <main
        className="main-content right-chat-active media-detail-page"
        style={{
          paddingLeft: "285px",
          paddingRight: "315px",
          paddingTop: "58px",
          minHeight: "100vh",
          transition: "all 0.3s ease",
        }}
      >
        <div className="middle-sidebar-bottom media-detail-middle-bottom">
          <div className="middle-sidebar-left media-detail-middle-left">
            <div className="media-detail-shell">
          <button
            type="button"
            className="detail-back-btn"
            onClick={() => navigate(`/club/${slug}`)}
          >
            <i className="feather-arrow-left" />
            Back to Club
          </button>

          {loading ? (
            <div className="detail-state-card">
              <div className="spinner-border text-primary" />
              <p>Loading media album...</p>
            </div>
          ) : mediaData?.can_view_media === 0 ? (
            <div className="detail-state-card">
              <div className="detail-state-icon">
                <i className="feather-lock" />
              </div>
              <h3>Media album is private</h3>
              <p>
                {mediaData?.private_message ||
                  "The club has restricted access to its media."}
              </p>
            </div>
          ) : !album ? (
            <div className="detail-state-card">
              <div className="detail-state-icon">
                <i className="feather-image" />
              </div>
              <h3>Album not found</h3>
              <p>This media album is unavailable or may have been removed.</p>
            </div>
          ) : (
            <>
              <section className="media-detail-hero">
                {(album.cover_image_url || album.cover_image) && (
                  <img
                    src={getImageUrl(
                      album.cover_image_url || album.cover_image
                    )}
                    alt={album.album_title || "Album cover"}
                  />
                )}

                <div className="media-detail-hero-overlay">
                  <span>
                    {clubProfile?.club_name ||
                      clubProfile?.client_name ||
                      "Bharat Sangam Club"}
                  </span>

                  <h1>{album.album_title || "Club Media"}</h1>

                  <p>
                    {album.album_description ||
                      album.description ||
                      "Photos, videos and memories from this club."}
                  </p>
                </div>
              </section>

              <section className="detail-card">
                <div className="detail-section-heading">
                  <div>
                    <h2>Album Information</h2>
                    <p>Complete information available for this media album.</p>
                  </div>
                </div>

                <div className="detail-info-grid">
                  <div className="detail-info-item">
                    <label>Category</label>
                    <strong>{album.album_category || "-"}</strong>
                  </div>

                  <div className="detail-info-item">
                    <label>Event Date</label>
                    <strong>{formatDate(album.event_date)}</strong>
                  </div>

                  <div className="detail-info-item">
                    <label>Location</label>
                    <strong>{album.location || "-"}</strong>
                  </div>

                  <div className="detail-info-item">
                    <label>Visibility</label>
                    <strong>{album.visibility || "-"}</strong>
                  </div>

                  {extraFields.map(([key, value]) => (
                    <div className="detail-info-item" key={key}>
                      <label>{niceLabel(key)}</label>
                      <strong>{String(value)}</strong>
                    </div>
                  ))}
                </div>
              </section>

              {images.length > 0 && (
                <section className="detail-card">
                  <div className="detail-section-heading">
                    <div>
                      <h2>Photos</h2>
                      <p>{images.length} photos in this album.</p>
                    </div>
                  </div>

                  <div className="album-photo-grid">
                    {images.map((image: string, index: number) => (
                      <a
                        href={getImageUrl(image)}
                        target="_blank"
                        rel="noreferrer"
                        key={`${image}-${index}`}
                      >
                        <img
                          src={getImageUrl(image)}
                          alt={`${album.album_title || "Album"} ${index + 1}`}
                          loading="lazy"
                        />
                      </a>
                    ))}
                  </div>
                </section>
              )}

              {videos.length > 0 && (
                <section className="detail-card">
                  <div className="detail-section-heading">
                    <div>
                      <h2>Videos</h2>
                      <p>{videos.length} video links in this album.</p>
                    </div>
                  </div>

                  <div className="album-video-list">
                    {videos.map((video: string, index: number) => (
                      <a
                        key={`${video}-${index}`}
                        href={video}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <span className="feather-play-circle" />
                        <div>
                          <strong>Video {index + 1}</strong>
                          <small>{video}</small>
                        </div>
                        <span className="feather-external-link" />
                      </a>
                    ))}
                  </div>
                </section>
              )}

              {(album.album_description || album.description) && (
                <section className="detail-card">
                  <div className="detail-section-heading">
                    <div>
                      <h2>About This Album</h2>
                    </div>
                  </div>

                  <p className="detail-description">
                    {album.album_description || album.description}
                  </p>
                </section>
              )}
            </>
          )}
            </div>
          </div>
        </div>
      </main>

      <Popupchat />
      <Appfooter />

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:wght@600;700&family=Inter:wght@400;500;600;700&display=swap');

        .main-content.right-chat-active.media-detail-page {
          width: 100%;
          font-family: 'Inter', sans-serif;
          background: linear-gradient(180deg, #f8f9fc 0%, #f1f3f8 100%);
          overflow-x: hidden;
        }

        .media-detail-middle-bottom,
        .media-detail-middle-left {
          width: 100%;
          max-width: 100%;
        }

        .media-detail-shell {
          margin-left: 22px;
          max-width: 900px;
          margin-top: 28px;
          padding-bottom: 40px;
        }

        .detail-back-btn {
          min-height: 34px;
          margin-bottom: 14px;
          padding: 0 11px;
          border: 1px solid #dce7f7;
          border-radius: 10px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          color: #2F6FE4;
          background: #ffffff;
          font-size: 11px;
          font-weight: 700;
          cursor: pointer;
        }

        .media-detail-hero {
          position: relative;
          min-height: 280px;
          margin-bottom: 18px;
          overflow: hidden;
          border: 1px solid #e9ecf2;
          border-radius: 16px;
          background: #eef1f6;
          box-shadow: 0 2px 8px rgba(15,23,42,.05);
        }

        .media-detail-hero > img {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .media-detail-hero-overlay {
          position: absolute;
          inset: 0;
          padding: 16px;
          display: flex;
          flex-direction: column;
          justify-content: flex-end;
          background: linear-gradient(
            to top,
            rgba(10,35,84,.88),
            rgba(10,35,84,.28) 62%,
            transparent
          );
        }

        .media-detail-hero-overlay > span {
          color: rgba(255,255,255,.82);
          font-size: 10.5px;
          font-weight: 600;
        }

        .media-detail-hero-overlay h1 {
          margin: 4px 0 0;
          color: #ffffff;
          font-family: 'Fraunces', serif;
          font-size: 24px;
          font-weight: 700;
          line-height: 1.3;
        }

        .media-detail-hero-overlay p {
          max-width: 650px;
          margin: 5px 0 0;
          color: rgba(255,255,255,.88);
          font-size: 11.5px;
          line-height: 1.5;
        }

        .detail-card {
          margin-bottom: 18px;
          padding: 16px;
          border: 1px solid #e9ecf2;
          border-radius: 16px;
          background: #ffffff;
          box-shadow: 0 2px 8px rgba(15,23,42,.045);
        }

        .detail-section-heading {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 12px;
          margin-bottom: 14px;
        }

        .detail-section-heading h2 {
          margin: 0;
          color: #0A2354;
          font-family: 'Fraunces', serif;
          font-size: 18px;
          font-weight: 700;
          line-height: 1.3;
        }

        .detail-section-heading p {
          margin: 4px 0 0;
          color: #8a93a3;
          font-size: 12px;
          line-height: 1.5;
        }

        .detail-info-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 8px;
        }

        .detail-info-item {
          min-width: 0;
          padding: 10px;
          border: 1px solid #edf1f6;
          border-radius: 10px;
          background: #f8fafc;
        }

        .detail-info-item label {
          display: block;
          margin-bottom: 4px;
          color: #94a3b8;
          font-size: 10.5px;
          font-weight: 600;
        }

        .detail-info-item strong {
          display: block;
          overflow: hidden;
          color: #0f1b34;
          font-size: 12.5px;
          font-weight: 600;
          line-height: 1.35;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .album-photo-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 8px;
        }

        .album-photo-grid a {
          display: block;
          aspect-ratio: 1 / .78;
          overflow: hidden;
          border: 1px solid #edf1f6;
          border-radius: 10px;
          background: #f8fafc;
        }

        .album-photo-grid img {
          width: 100%;
          height: 100%;
          display: block;
          object-fit: cover;
          transition: transform .2s ease;
        }

        .album-photo-grid a:hover img {
          transform: scale(1.03);
        }

        .album-video-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .album-video-list a {
          min-width: 0;
          padding: 10px;
          border: 1px solid #edf1f6;
          border-radius: 10px;
          display: grid;
          grid-template-columns: 32px minmax(0,1fr) 20px;
          align-items: center;
          gap: 8px;
          color: #2F6FE4;
          background: #f8fafc;
          text-decoration: none;
        }

        .album-video-list strong {
          display: block;
          color: #0f1b34;
          font-size: 12px;
          font-weight: 600;
        }

        .album-video-list small {
          display: block;
          margin-top: 2px;
          overflow: hidden;
          color: #94a3b8;
          font-size: 9.5px;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .detail-description {
          margin: 0;
          color: #7c8494;
          font-size: 12.5px;
          line-height: 1.65;
          white-space: pre-line;
        }

        .detail-state-card {
          width: 100%;
          min-height: 200px;
          padding: 28px 22px;
          border: 1px dashed #dbe2ec;
          border-radius: 16px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          background: #ffffff;
        }

        .detail-state-icon {
          width: 44px;
          height: 44px;
          margin-bottom: 11px;
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #5f7fae;
          background: #eef4fb;
          font-size: 20px;
        }

        .detail-state-card h3 {
          margin: 0;
          color: #475569;
          font-size: 14px;
          font-weight: 700;
        }

        .detail-state-card p {
          max-width: 330px;
          margin: 6px 0 0;
          color: #94a3b8;
          font-size: 11.5px;
          line-height: 1.55;
        }

        @media (max-width: 1400px) {
          .main-content.right-chat-active.media-detail-page {
            padding-right: 290px !important;
          }
        }

        @media (max-width: 1200px) {
          .main-content.right-chat-active.media-detail-page {
            padding-left: 20px !important;
            padding-right: 20px !important;
          }

          .media-detail-shell {
            margin-left: 0;
            max-width: 100%;
          }
        }

        @media (max-width: 768px) {
          .main-content.right-chat-active.media-detail-page {
            padding-top: 16px !important;
            padding-left: 12px !important;
            padding-right: 12px !important;
          }

          .media-detail-middle-bottom,
          .media-detail-middle-left {
            padding-left: 0 !important;
            padding-right: 0 !important;
            margin-left: 0 !important;
            margin-right: 0 !important;
          }

          .media-detail-shell {
            margin-top: 0;
            margin-left: 0;
            max-width: 100%;
            padding-bottom: 76px;
          }

          .media-detail-hero {
            min-height: 230px;
            border-radius: 14px;
          }

          .media-detail-hero-overlay h1 {
            font-size: 21px;
          }

          .detail-card {
            padding: 13px;
            border-radius: 14px;
          }

          .detail-info-grid {
            grid-template-columns: 1fr;
          }

          .album-photo-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }
        }

        @media (max-width: 480px) {
          .album-photo-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </Fragment>
  );
};

export default MediaAlbumDetailsPage;
